import { CheckIcon, CoinsIcon, LockIcon, SparklesIcon } from "lucide-react";

import type { ShopCatalogEntry } from "./types";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";

import { stafferPetShop } from "@/lib/stafferPetShop";
import { cn } from "@/lib/utils";

export const ShopItemTile = ({
  coinBalance,
  entry,
  onSelect,
}: {
  coinBalance: number;
  entry: ShopCatalogEntry;
  onSelect: (id: string) => void;
}) => {
  const isLocked = entry.group === "LOCKED";
  const isShort = entry.group === "FOR_SALE" && entry.price > coinBalance;

  return (
    <Button
      type="button"
      variant="outlineDark"
      disabled={isLocked}
      onClick={() => {
        onSelect(entry.id);
      }}
      className={cn(
        "relative h-auto w-full flex-col items-stretch gap-1.5 whitespace-normal rounded-none border-2 border-slate-900 bg-white p-1.5 text-left text-slate-900 shadow-[3px_3px_0_#172033] hover:bg-orange-50 disabled:opacity-100",
        entry.isSelected &&
          "translate-x-0.5 translate-y-0.5 bg-orange-100 shadow-[1px_1px_0_#172033] outline outline-[3px] outline-orange-500",
      )}
    >
      <Column
        className={cn(
          "aspect-square w-full items-center justify-center overflow-hidden border-2 border-slate-900 bg-[#dff3ff]",
          isLocked && "bg-slate-300",
          isShort && "opacity-60",
        )}
      >
        {entry.art}
      </Column>
      {entry.slotTag ? (
        <Text className="absolute left-3 top-3 bg-slate-900 px-1 font-mono text-[8px] font-black tracking-[0.08em] text-white">
          {entry.slotTag}
        </Text>
      ) : null}
      <Text
        className={cn(
          "line-clamp-2 min-h-[2.5em] font-mono text-[10px] font-black uppercase leading-tight tracking-[0.04em]",
          isLocked ? "text-slate-600" : "text-slate-900",
        )}
      >
        {isLocked ? "???" : entry.label}
      </Text>
      <Row className="items-center gap-1">
        <ShopItemTileStatus
          coinBalance={coinBalance}
          entry={entry}
          isShort={isShort}
        />
      </Row>
    </Button>
  );
};

const ShopItemTileStatus = ({
  coinBalance,
  entry,
  isShort,
}: {
  coinBalance: number;
  entry: ShopCatalogEntry;
  isShort: boolean;
}) => {
  /* Render */
  const render = () => {
    if (entry.group === "LOCKED") {
      return (
        <Row className="min-w-0 items-center gap-1 bg-slate-700 px-1 py-0.5 font-mono text-[8px] font-black uppercase text-white">
          <LockIcon className="h-2.5 w-2.5 shrink-0" />
          <Text className="truncate font-mono text-[8px] font-black uppercase text-white">
            {entry.lockedNote}
          </Text>
        </Row>
      );
    }
    if (entry.group === "WEARING") {
      return (
        <Row className="items-center gap-0.5 font-mono text-[9px] font-black uppercase text-orange-600">
          <SparklesIcon className="h-3 w-3" />
          Wearing
        </Row>
      );
    }
    if (entry.group === "OWNED") {
      return (
        <Row className="items-center gap-0.5 font-mono text-[9px] font-black uppercase text-emerald-700">
          <CheckIcon className="h-3 w-3" />
          {entry.ownedLabel}
        </Row>
      );
    }
    if (isShort) {
      return (
        <Text className="font-mono text-[9px] font-black uppercase text-slate-500">
          Need {(entry.price - coinBalance).toString()}
        </Text>
      );
    }
    return (
      <Row className="items-center gap-0.5 border-2 border-slate-900 bg-amber-300 px-1 font-mono text-[10px] font-black text-slate-900">
        <CoinsIcon className="h-3 w-3" />
        {stafferPetShop.getPriceLabel(entry.price)}
      </Row>
    );
  };

  return render();
};
